import type {
    Breakpoint,
    BreakpointId,
    BreakpointRange,
    BreakpointsData,
    BreakpointsMap,
    ComponentExportStructure,
    CoreExportFormat,
    ReferencedItem,
} from '@wix/document-services-types'
import _ from 'lodash'

const BREAKPOINT_RANGE_TYPE: string = 'BreakpointRange'

export interface BreakpointWithOwner extends Breakpoint {
    ownerComponentId?: string
}

export type BreakpointsWithOwnerMap = Record<BreakpointId, BreakpointWithOwner>

const getBreakpointsFromDependencies = (componentExport: CoreExportFormat): BreakpointsMap => {
    const variantRefs = componentExport.dependencies?.references?.variants ?? {}
    const breakpointRefs = _(variantRefs)
        .pickBy(
            (ref: ReferencedItem): ref is Required<ReferencedItem> => ref.type === BREAKPOINT_RANGE_TYPE && !!ref.info,
        )
        .mapValues('info')
        .value()

    return breakpointRefs
}

const getBreakpointsFromComponent = (
    componentId: string,
    component: ComponentExportStructure,
): BreakpointsWithOwnerMap => {
    const breakpointVariants = component.breakpointVariants as BreakpointsData | undefined
    if (!breakpointVariants?.values) {
        return {}
    }

    return _(breakpointVariants.values)
        .keyBy('id')
        .mapValues((bp: BreakpointRange) => ({
            ..._.pick(bp, ['min', 'max', 'canvasSize']),
            ownerComponentId: componentId,
        }))
        .value()
}

export const getBreakpointData = (componentExport: CoreExportFormat): BreakpointsWithOwnerMap => {
    const dependencyBreakpoints: BreakpointsWithOwnerMap = getBreakpointsFromDependencies(componentExport)

    const componentsBreakpoints: BreakpointsWithOwnerMap[] = _.map(componentExport.components, (component, id) =>
        getBreakpointsFromComponent(id, component),
    )

    return _.assign(dependencyBreakpoints, ...componentsBreakpoints)
}
